import { sharedSchema } from "@shared-database/pgSchema";
import { KnowledgeCommunity } from "@shared-database/models/rag/knowledge-community";
import {
  leoID,
  leoNotNullForeignID,
  timestampColumns,
} from "@shared-database/utils";
import type { InferInsertModel, InferSelectModel } from "drizzle-orm";
import { index, integer, text, uniqueIndex } from "drizzle-orm/pg-core";

/**
 * One finding from a GraphRAG-style community report. Each community
 * has 0-N findings; ordering within a report is preserved via
 * `orderIndex` so the UI and global search render them in the same
 * sequence the LLM emitted. Rows cascade-delete with the community so
 * a re-summarisation transaction can `DELETE WHERE communityId = ?`
 * followed by a bulk insert without orphaning rows.
 */
export const KnowledgeCommunityFinding = sharedSchema.table(
  "KnowledgeCommunityFinding",
  {
    // Mixins
    ...timestampColumns,

    // Properties
    id: leoID("id"),

    /**
     * 0-based position within the community report. Preserves the
     * LLM-authored ordering (most-salient first) across the delete +
     * bulk-insert rewrite used on re-summarisation.
     */
    orderIndex: integer("orderIndex").notNull(),

    /** Short headline for the finding (LLM-written). */
    summary: text("summary").notNull(),
    /** Supporting paragraph grounding the summary in members/relations. */
    explanation: text("explanation").notNull(),

    // References
    communityId: leoNotNullForeignID("communityId").references(
      () => KnowledgeCommunity.id,
      { onDelete: "cascade" },
    ),
  },
  (table) => [
    index("KnowledgeCommunityFinding_communityIdx").on(table.communityId),
    uniqueIndex("KnowledgeCommunityFinding_communityOrderUniqueIdx").on(
      table.communityId,
      table.orderIndex,
    ),
  ],
);

// Types
export type KnowledgeCommunityFindingType = InferSelectModel<
  typeof KnowledgeCommunityFinding
>;
export type KnowledgeCommunityFindingInsertType = InferInsertModel<
  typeof KnowledgeCommunityFinding
>;
